import {
  DialogRef,
  TMSPalletStatus,
  TMSScanOrderPalletVo,
  TMSShippingLabelMode,
} from "@/types";
import { Alert, Button, Modal, Tag } from "antd";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { getScanOrderPallet } from "./helper";
import Indicator from "@/components/shared/Indicator";
import classNames from "classnames";
import { OrderAPI, submitScanPalletProduct } from "@/apis/tms/order";
import { LoadingMask } from "@/components/shared/LoadingSection";
import { useGlobalContext } from "@/hooks/context";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import {
  DefaultPalletScanLabelConfig,
  PalletScanLabelConfig,
} from "./config/helper";
import PalletScanConfig from "./config";
import { PalletScannedProducts } from "./scanned";
import CurrentScanning from "./current";
import { delay } from "@/utils/delay";
import { CurrentScaningRef } from "./current/helper";
import ShippingLabelInfo, {
  ShippingLabelInfoRef,
} from "../../order/process/ShippingLabelInfo";
import PdfViewerModal from "@/components/shared/PdfViewer";

interface PalletScanProductsProps {
  className?: string;
  onPrintCartonLabel: (
    orderId: string,
    palletId: string,
    scannedCartonId: number,
  ) => Promise<void>;
  onScannedCarton: () => void;
  onCompletePallet: (
    orderId: string,
    palletId: string,
    palletNo: string,
    requireDimension: boolean,
    printPalletLabel: boolean,
    printShippingLabel: boolean,
  ) => void;
}

interface DialogOpenProps {
  palletId: string;
  orderId: string;
}

const SubmittingInfo = <span className="text-[32px]">Processing...</span>;
const ProductScannedInfo = (
  <div className="flex flex-row gap-2 items-center">
    <CheckCircleOutlined className="!text-green-500 !text-[46px]" />
    <span>Scanned carton into pallet successfully</span>
  </div>
);
const FailedToScanProductInfo = (message: string) => {
  return (
    <div className="flex flex-row gap-2 items-start">
      <CloseCircleOutlined className="!text-red-500 !text-[46px] pt-1" />
      <div className="max-w-[500px] break-all">Failed: {message}</div>
    </div>
  );
};
const PrintingCartonLabelInfo = (
  <div className="flex flex-row gap-2 items-center">
    <PrinterOutlined className="!text-blue-500 !text-[46px]" />
    <span>Printing Carton Label...</span>
  </div>
);

const PalletScanProducts = forwardRef<DialogRef, PalletScanProductsProps>(
  (props, ref) => {
    const { className, onPrintCartonLabel, onScannedCarton, onCompletePallet } =
      props;
    const { hintPrompt } = useGlobalContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [palletId, setPalletId] = useState<string | undefined>(undefined);
    const [orderId, setOrderId] = useState<string | undefined>(undefined);
    const shippingLabelInfoRef = useRef<ShippingLabelInfoRef>(null);
    const [extraConfig, setExtraConfig] = useState<PalletScanLabelConfig>(
      DefaultPalletScanLabelConfig,
    );
    const [isSumitting, setIsSumitting] = useState(false);
    const currentScanningRef = useRef<CurrentScaningRef>(null);
    const pdfViewerModalRef = useRef<DialogRef>(null);
    const [submitInfo, setSubmitInfo] =
      useState<React.ReactNode>(SubmittingInfo);
    const [scanOrderPallet, setScanOrderPallet] =
      useState<TMSScanOrderPalletVo | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const productScanSerialNoMap = useMemo(() => {
      if (scanOrderPallet == null) return {};
      const map = scanOrderPallet.products.map(one => [one.productBarcode, one.extra?.scanSerialNo ?? false]);
      return Object.fromEntries(map);
    }, [scanOrderPallet]);

    const fetchPalletData = useCallback(
      async (palletId: string, orderId: string) => {
        return getScanOrderPallet(orderId, palletId).then((data) => {
          setScanOrderPallet(data);
          return data;
        });
      },
      [],
    );

    const openDialog = useCallback(
      (props: unknown) => {
        const { palletId, orderId } = props as DialogOpenProps;
        setPalletId(palletId);
        setOrderId(orderId);
        setIsModalOpen(true);
        setIsLoading(true);
        fetchPalletData(palletId, orderId).then((res) => {
          if (res) {
            setIsLoading(false);
            setExtraConfig({
              ...DefaultPalletScanLabelConfig,
              autoPrintCartonLabel: res.scanConfig.hasCartonLabel
                ? true
                : false,
              autoPrintPalletLabel: res.scanConfig.applyPalletLabel
                ? true
                : false,
            });
          }
        });
        currentScanningRef.current?.reset(palletId, orderId);
      },
      [fetchPalletData],
    );

    const closeDialog = useCallback(() => {
      setIsModalOpen(false);
    }, []);

    const submitPalletProduct = useCallback(
      async (
        orderId: string,
        palletId: string,
        sku: string,
        serialNo?: string,
        printCartonLabel?: boolean,
      ): Promise<void> => {
        setIsSumitting(true);
        setSubmitInfo(SubmittingInfo);
        return submitScanPalletProduct(orderId, palletId, sku, serialNo)
          .then((scannedProduct) => {
            hintPrompt(
              "success",
              "Submit Success",
              "Product submitted successfully",
            );
            setSubmitInfo(ProductScannedInfo);
            onScannedCarton();
            return delay(async () => {
              if (printCartonLabel) {
                setSubmitInfo(PrintingCartonLabelInfo);
                return onPrintCartonLabel(orderId, palletId, scannedProduct.id)
                  .then(() => {
                    fetchPalletData(palletId, orderId);
                    return delay(() => setIsSumitting(false), 1000);
                  })
                  .catch((error) => {
                    hintPrompt(
                      "error",
                      "Failed to print carton label",
                      error.msg ?? "Unknown error",
                    );
                    throw error;
                  });
              } else {
                fetchPalletData(palletId, orderId);
                return delay(() => setIsSumitting(false), 1000);
              }
            }, 1000);
          })
          .catch((error) => {
            hintPrompt("error", "Submit Failed", error.msg ?? "Unknown error");
            setSubmitInfo(
              FailedToScanProductInfo(error.msg ?? "Unknown error"),
            );
            console.error(error);
            return delay(() => setIsSumitting(false), 2000);
          });
      },
      [hintPrompt, fetchPalletData, onPrintCartonLabel, onScannedCarton],
    );

    useImperativeHandle(
      ref,
      () => ({
        showModal: openDialog,
        hideModal: closeDialog,
      }),
      [openDialog, closeDialog],
    );

    return (
      <Modal
        className={className}
        title={
          isLoading
            ? "Loading pallet info..."
            : scanOrderPallet == null
              ? "Error happened"
              : null
        }
        zIndex={100}
        open={isModalOpen}
        closeIcon={null}
        width={scanOrderPallet != null ? "84%" : "36%"}
        onCancel={closeDialog}
        footer={null}
        loading={isLoading}
        maskClosable={false}
      >
        <div className="h-full">
          {scanOrderPallet != null ? (
            <div className="w-full">
              <div className="text-[24px] font-bold pl-2 flex flex-row gap-2 items-center">
                Pallet {scanOrderPallet.no}
                <Tag color="blue" className="!text-xl !mr-0">
                  {scanOrderPallet.status}
                </Tag>
                <ShippingLabelInfo
                  className="text-lg"
                  orderId={scanOrderPallet.orderId}
                  ref={shippingLabelInfoRef}
                />
              </div>
              <Indicator label="Scan Config" />
              <PalletScanConfig
                scanConfig={scanOrderPallet!.scanConfig}
                extraConfig={extraConfig}
                changeExtraConfig={(key, value) => {
                  setExtraConfig((prev) => ({
                    ...prev,
                    [key]: value,
                  }));
                }}
              />
              <div className="flex flex-row gap-2">
                <div className="w-1/2">
                  <Indicator
                    label={`Scanned ${scanOrderPallet.scannedProducts.length} products`}
                    hint="scroll vertically to view all products"
                  />
                  <div
                    className={classNames(
                      "border-[1px] border-gray-200",
                      "rounded-md h-[360px] overflow-auto",
                    )}
                  >
                    {scanOrderPallet.scannedProducts.length > 0 ? (
                      <PalletScannedProducts
                        orderId={scanOrderPallet.orderId}
                        palletId={scanOrderPallet.id}
                        products={scanOrderPallet.products}
                        scannedProducts={scanOrderPallet.scannedProducts}
                        printCartonLabel={onPrintCartonLabel}
                        hasCartonLabel={
                          scanOrderPallet.scanConfig.hasCartonLabel
                        }
                        shippingLabelMode={
                          scanOrderPallet.scanConfig.shippingLabelMode
                        }
                        onDeleteSuccess={() => {
                          fetchPalletData(
                            scanOrderPallet.orderId,
                            scanOrderPallet.id,
                          );
                          shippingLabelInfoRef.current?.refresh();
                          onScannedCarton();
                        }}
                      />
                    ) : (
                      <div className="m-4">
                        <Alert
                          type="warning"
                          showIcon
                          description="No products scanned yet!"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-1/2">
                  <Indicator label="Current Scanning" />
                  <CurrentScanning
                    ref={currentScanningRef}
                    className="border-[1px] border-gray-200 rounded-md h-[360px]"
                    palletId={scanOrderPallet.id}
                    orderId={scanOrderPallet.orderId}
                    productScanSerialNoMap={productScanSerialNoMap}
                    autoPrintCartonLabel={
                      scanOrderPallet.scanConfig.hasCartonLabel &&
                      extraConfig.autoPrintCartonLabel
                    }
                    scanMode={extraConfig.scanMode}
                    onError={() => {}}
                    submitPalletProduct={submitPalletProduct}
                  />
                </div>
              </div>
            </div>
          ) : (
            <Alert type="error" showIcon description="Pallet not found!" />
          )}
          <div className="flex justify-end mt-4 gap-2">
            <Button danger onClick={closeDialog}>
              Close
            </Button>
            <Button
              onClick={() => {
                pdfViewerModalRef.current?.showModal({
                  url: `${OrderAPI.config.baseURL}/scan/${orderId}/pallets/${palletId}/label?includeShippingLabel=false`,
                });
              }}
              variant="outlined"
              color="green"
              icon={<PrinterOutlined />}
            >
              Label
            </Button>
            <Button
              disabled={
                scanOrderPallet == null ||
                scanOrderPallet.status !== TMSPalletStatus.Upcoming
              }
              loading={isSumitting}
              variant="solid"
              color="blue"
              onClick={() => {
                onCompletePallet(
                  orderId!,
                  palletId!,
                  scanOrderPallet!.no!,
                  scanOrderPallet!.scanConfig.requirePalletDimension,
                  extraConfig.autoPrintPalletLabel,
                  scanOrderPallet!.scanConfig.shippingLabelMode ===
                    TMSShippingLabelMode.Pallet,
                );
              }}
            >
              Complete Pallet
            </Button>
          </div>
        </div>
        {isSumitting && (
          <LoadingMask
            info={submitInfo}
            className="bg-gray-100/70 text-[32px] font-bold z-50"
          />
        )}
        <PdfViewerModal ref={pdfViewerModalRef} />
      </Modal>
    );
  },
);

export default PalletScanProducts;
